import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { cachedGet } from "../api";
import { useAmazonContext } from "../../Contexts/AmazonContext";
import { Home, BookOpen, User } from "lucide-react";
import LibraryProgressBar from "./LibraryProgressBar";
import styled from "styled-components";
import SideNavRowQuiz from "../sidebar/SideNavRowQuiz";
import SideNavRowLesson from "../sidebar/SideNavRowLesson";
import { motion } from "framer-motion";
import { Header, NavButton } from "./landingPageSharedStyledComponents";
import CourseProgressBar from "../sidebar/CourseProgressBar";
import BottomNavBar from "./BottomNavBar"; // Import the new BottomNavBar component
import LottieAnimation from "../LottieAnimation";
import CourseCertificateInfographic, {
  formatCertificateDate,
} from "../lessonNotes/CourseCertificateInfographic";
import CertificateShareButton from "../lessonNotes/CertificateShareButton";
import CertificateShareSurfaces from "../lessonNotes/CertificateShareSurfaces";
import {
  useCertificateRecipientName,
  useCertificateShare,
} from "../lessonNotes/useCertificateShare";
import "../lessonNotes/LessonNotes.css";

const LibraryContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
  max-height: calc(
    100vh - 150px
  ); // Adjust based on your header and footer heights
`;

const LottieContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100vw;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  background-color: #fff; // Optional: add a background color

  > div {
    width: 100%;
    max-width: 300px; // Adjust this value as needed

    @media only screen and (min-width: 601px) {
      max-width: 50%;
    }
  }
`;

const BookWrapper = styled.div`
  display: flex;
  flex-direction: column;
  /* align-items: center; */
  width: 100%;
  padding: 10px;
  margin-bottom: 30px;
  /* background-color: #d2cfe6; */
  border-radius: 0.5rem;
`;

const BookCover = styled.div`
  flex: 0 0 auto;
  width: 200px;
  margin: 10px auto;

  scroll-snap-align: start;
  position: relative;
  overflow: visible;
`;

const CoverImage = styled.img`
  width: 100%;

  height: ${200 * (2250 / 1410)}px; // Maintain aspect ratio
  object-fit: cover;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0 10px 15px rgba(0, 0, 0, 0.2);
  }
`;

const TabContainer = styled.div`
  display: flex;
  width: min(420px, 90%);
  background: #f5f5f5;
  border-radius: 12px;
  margin: 12px auto 8px;
  padding: 4px;
  gap: 3px;
  flex: none;
`;

const Tab = styled.button`
  flex: 1;
  padding: 10px 6px;
  border: none;
  border-radius: 8px;
  font-family: "Quicksand", sans-serif;
  font-weight: bold;
  font-size: 13px;
  line-height: 1.2;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  background: ${(props) => (props.$active ? "#8747d4" : "transparent")};
  color: ${(props) => (props.$active ? "white" : "#666")};
  box-shadow: ${(props) =>
    props.$active ? "0 2px 4px rgba(135, 71, 212, 0.3)" : "none"};

  &:hover {
    background: ${(props) =>
      props.$active ? "#8747d4" : "rgba(135, 71, 212, 0.1)"};
    color: ${(props) => (props.$active ? "white" : "#8747d4")};
  }
`;

const EmptyLibraryMessage = styled.p`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 180px;
  font-size: 1.5rem;
  text-align: center;
  padding: 20px;
`;

// Holds the last rendered library view so navigating back to this tab paints immediately
// rather than replaying the whole fetch chain behind a loading animation.
let lastLibraryView = null; // { userEmail, startedCourses, courseData }

function Library() {
  const userEmail = localStorage.getItem("userEmail");
  const cachedView =
    lastLibraryView && lastLibraryView.userEmail === userEmail
      ? lastLibraryView
      : null;

  // The course list and its chapter data are committed together. Rendering the list
  // before its chapter data would file every course under In Progress with empty bars.
  const [libraryView, setLibraryView] = useState(() => ({
    startedCourses: cachedView?.startedCourses || [],
    courseData: cachedView?.courseData || {},
  }));
  const { startedCourses, courseData } = libraryView;
  const {
    progressData,
    chapterProgressData,
    setProgressData,
    setChapterProgressData,
    isDarkMode,
  } = useAmazonContext();
  const [isLoading, setIsLoading] = useState(!cachedView);
  const [activeTab, setActiveTab] = useState("inProgress");
  const [certificateDate] = useState(() => formatCertificateDate());
  const recipientName = useCertificateRecipientName(true);
  const certificateShare = useCertificateShare();

  useEffect(() => {
    if (!userEmail) {
      console.error("User email not found in local storage");
      setIsLoading(false);
      return;
    }

    // The course list does not depend on progress, so it starts here instead of waiting
    // for the progress calls. cachedGet dedupes, so the effect below reuses this request
    // rather than issuing a second one.
    cachedGet("/coursesData", { ttl: 300000 }).catch(() => {});

    cachedGet("/fetchProgressRecord", { params: { userEmail } })
      .then(setProgressData)
      .catch((error) => {
        console.error("Error fetching progress data:", error);
        setIsLoading(false);
      });

    cachedGet("/fetchChapterProgressRecord", { params: { userEmail } })
      .then(setChapterProgressData)
      .catch((error) => {
        console.error("Error fetching chapter progress data:", error);
        setIsLoading(false);
      });
  }, [userEmail]);

  useEffect(() => {
    let cancelled = false;

    const fetchStartedCourses = async () => {
      if (!progressData || !chapterProgressData) {
        // Still waiting on the progress calls. Leave the spinner (or the cached view) up
        // rather than filling the screen in piece by piece.
        return;
      }

      try {
        const allCourses = await cachedGet("/coursesData", { ttl: 300000 });

        const coursesWithProgress = allCourses.filter((course) => {
          const courseProgress = progressData.find(
            (progress) =>
              progress.courseProgress === course.courseTitle.replace(/\s/g, "")
          );
          return (
            courseProgress &&
            courseProgress.lessonProgress &&
            courseProgress.lessonProgress.length > 0
          );
        });

        // Fetch detailed data for each started course
        const courseDataPromises = coursesWithProgress.map(async (course) => {
          const courseTitle = course.courseTitle.replace(/\s/g, "");
          const data = await cachedGet(
            `/${courseTitle}/sideBarData?collectionName=${course.courseTopicsCollectionName}&modelName=${course.courseTopicsModelName}`,
            { ttl: 300000 }
          );
          return { courseTitle, data };
        });

        const allCourseData = await Promise.all(courseDataPromises);
        if (cancelled) return;

        const courseDataObj = {};
        allCourseData.forEach(({ courseTitle, data }) => {
          courseDataObj[courseTitle] = simplifyData(data);
        });
        setLibraryView({
          startedCourses: coursesWithProgress,
          courseData: courseDataObj,
        });

        lastLibraryView = {
          userEmail,
          startedCourses: coursesWithProgress,
          courseData: courseDataObj,
        };
      } catch (error) {
        console.error("Error fetching started courses:", error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchStartedCourses();

    return () => {
      cancelled = true;
    };
  }, [progressData, chapterProgressData]);

  function simplifyData(data) {
    return (
      data &&
      data
        .map((topicHeader) => {
          return topicHeader.entries.map((chapter) => {
            return {
              chapterNumber: chapter.topicNumber,
              chapterTitle: chapter.title,
              lessons: chapter.entries[0].entries.map((lesson) => {
                return {
                  lessonNumber: lesson.sectionNumber,
                  lessonTitle: lesson.title,
                  lessonImage: lesson.sectionImage,
                };
              }),
            };
          });
        })
        .flat()
    );
  }

  function getCourseDetails(courseTitle, simplifiedData) {
    const courseProgressData = progressData.find(
      (progress) => progress.courseProgress === courseTitle
    );

    // If there's no course progress data or simplified data, return null
    if (!courseProgressData || !simplifiedData) {
      return null;
    }

    const lessonProgress = courseProgressData.lessonProgress;
    const lastCompletedLesson = lessonProgress[lessonProgress.length - 1];

    const chapterOfLastCompletedLesson = simplifiedData.find((chapter) =>
      chapter.lessons.some(
        (lesson) => lesson.lessonNumber === lastCompletedLesson
      )
    );

    if (!chapterOfLastCompletedLesson) return null;

    const { lessons } = chapterOfLastCompletedLesson;
    const lastCompletedLessonIndex = lessons.findIndex(
      (lesson) => lesson.lessonNumber === lastCompletedLesson
    );

    // Check if this chapter's quiz is completed (if chapter progress data exists)
    const courseChapterProgressData = chapterProgressData?.find(
      (progress) => progress.courseProgress === courseTitle
    );

    const chapterNumber = chapterOfLastCompletedLesson.chapterNumber;
    const isChapterQuizCompleted =
      courseChapterProgressData?.chapterProgress?.includes(chapterNumber) ||
      false;

    const isLastLessonOfChapter =
      lastCompletedLessonIndex === lessons.length - 1;
    const isFirstLessonOfChapter = lastCompletedLessonIndex === 0;

    let nextLessonDetails;
    if (isLastLessonOfChapter && !isChapterQuizCompleted) {
      // Show quiz for current chapter
      nextLessonDetails = {
        type: "quiz",
        chapterNumber: chapterOfLastCompletedLesson.chapterNumber,
        chapterTitle: chapterOfLastCompletedLesson.chapterTitle,
      };
    } else if (isChapterQuizCompleted) {
      // Show first lesson of next chapter
      const nextChapterIndex =
        simplifiedData.findIndex(
          (chapter) =>
            chapter.chapterNumber === chapterOfLastCompletedLesson.chapterNumber
        ) + 1;
      if (nextChapterIndex < simplifiedData.length) {
        const nextChapter = simplifiedData[nextChapterIndex];
        const firstLesson = nextChapter.lessons[0];
        nextLessonDetails = {
          type: "lesson",
          lessonNumber: firstLesson.lessonNumber,
          lessonTitle: firstLesson.lessonTitle,
          lessonImage: firstLesson.lessonImage,
        };
      }
    } else {
      // Show next lesson in current chapter
      const nextLesson = lessons[lastCompletedLessonIndex + 1];
      nextLessonDetails = {
        type: "lesson",
        lessonNumber: nextLesson.lessonNumber,
        lessonTitle: nextLesson.lessonTitle,
        lessonImage: nextLesson.lessonImage,
      };
    }

    return {
      isLastLessonOfChapter,
      isFirstLessonOfChapter,
      chapterOfLastCompletedLesson,
      nextLessonDetails,
      isLessonProgressEmpty: lessonProgress.length === 0,
    };
  }

  function isCourseCompleted(courseTitle, simplifiedData) {
    if (!simplifiedData?.length || !chapterProgressData) return false;
    const lastChapter = simplifiedData[simplifiedData.length - 1];
    const courseChapterProgress = chapterProgressData.find(
      (progress) => progress.courseProgress === courseTitle
    );
    return Boolean(
      courseChapterProgress?.chapterProgress?.includes(lastChapter.chapterNumber)
    );
  }

  const convertToCloudFrontUrl = (s3Url) => {
    if (!s3Url) return s3Url;
    return s3Url.replace(
      "https://hhcourses-assets.s3.us-east-2.amazonaws.com",
      "https://d10jmd9bhq0s4v.cloudfront.net"
    );
  };

  if (isLoading) {
    return (
      <div>
        <LottieContainer>
          <LottieAnimation
            lottieFile={convertToCloudFrontUrl(
              "https://hhcourses-assets.s3.us-east-2.amazonaws.com/General/LottieFiles/loading.json"
            )}
            loop={true}
          ></LottieAnimation>
        </LottieContainer>
        <BottomNavBar></BottomNavBar>
      </div>
    );
  }

  const inProgressCourses = startedCourses.filter((course) => {
    const courseTitle = course.courseTitle.replace(/\s/g, "");
    return !isCourseCompleted(courseTitle, courseData[courseTitle]);
  });
  const completedCourses = startedCourses.filter((course) => {
    const courseTitle = course.courseTitle.replace(/\s/g, "");
    return isCourseCompleted(courseTitle, courseData[courseTitle]);
  });
  const visibleCourses =
    activeTab === "completed" ? completedCourses : inProgressCourses;

  const renderCourseCard = (course) => {
    const courseTitle = course.courseTitle.replace(/\s/g, "");
    const simplifiedData = courseData[courseTitle];
    const courseDetails = getCourseDetails(courseTitle, simplifiedData);
    const courseCompleted = isCourseCompleted(courseTitle, simplifiedData);
    const courseCardTitle = course.courseCardTitle || course.courseTitle;
    const courseCover = convertToCloudFrontUrl(course.courseImage || "");

    return (
      <BookWrapper key={course.id}>
        {courseCompleted ? (
          <>
            <BookCover>
              <CoverImage
                src={courseCover}
                alt={`${course.courseTitle} cover`}
              />
              <div className="course-certificate-preview course-certificate-preview--library">
                {recipientName ? (
                  <div className="course-certificate-preview-scale">
                    <CourseCertificateInfographic
                      recipientName={recipientName}
                      courseTitle={courseCardTitle}
                      courseCover={courseCover}
                      givenOn={certificateDate}
                    />
                  </div>
                ) : (
                  <div className="course-certificate-preview-placeholder" />
                )}
              </div>
            </BookCover>
            <CertificateShareButton
              compact
              disabled={!recipientName}
              onClick={() =>
                certificateShare.startShare({
                  recipientName,
                  courseCardTitle,
                  courseCover,
                  givenOn: certificateDate,
                })
              }
            />
          </>
        ) : (
          <BookCover>
            <CoverImage
              src={courseCover}
              alt={`${course.courseTitle} cover`}
            />
          </BookCover>
        )}

        {!courseCompleted && (
          <CourseProgressBar
            courseTitleUserIsOn={courseTitle}
            progressData={progressData}
            chapterProgressData={chapterProgressData}
            simplifiedChaptersLessonsData={simplifiedData}
          />
        )}

        {!courseCompleted && courseDetails?.nextLessonDetails && (
          <div className="ContinueSideNavContainer">
            {courseDetails.nextLessonDetails.type === "quiz" ? (
              <Link
                to={`/${courseTitle}/quiz/${courseDetails.nextLessonDetails.chapterNumber}`}
              >
                <motion.div
                  whileTap={{ scale: 0.8 }}
                  transition={{
                    type: "spring",
                    stiffness: 400,
                    damping: 17,
                  }}
                >
                  <motion.div
                    animate={{ y: [-5, 5, -5] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <SideNavRowQuiz
                      number={courseDetails.nextLessonDetails.chapterNumber}
                      title={courseDetails.nextLessonDetails.chapterTitle}
                      width="90%"
                      marginLeft="5%"
                    />
                  </motion.div>
                </motion.div>
              </Link>
            ) : (
              <Link
                to={`/${courseTitle}/lesson/${courseDetails.nextLessonDetails.lessonNumber}`}
              >
                <motion.div
                  whileTap={{ scale: 0.8 }}
                  transition={{
                    type: "spring",
                    stiffness: 400,
                    damping: 17,
                  }}
                >
                  <motion.div
                    animate={{ y: [-5, 5, -5] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <SideNavRowLesson
                      backgroundColor="white"
                      image={courseDetails.nextLessonDetails.lessonImage}
                      number={courseDetails.nextLessonDetails.lessonNumber}
                      title={courseDetails.nextLessonDetails.lessonTitle}
                      width="90%"
                      marginLeft="5%"
                      borderRadius="14% 2% 2% 14% / 50% 10% 10% 50%"
                    />
                  </motion.div>
                </motion.div>
              </Link>
            )}
          </div>
        )}
      </BookWrapper>
    );
  };

  const emptyMessage = !startedCourses.length
    ? "You haven't started any courses yet!"
    : activeTab === "completed"
      ? "You haven't completed any courses yet!"
      : "No courses in progress.";

  return (
    <div>
      <Header className="sideNavChapterHeader">
        <p>My Library</p>
      </Header>
      <LibraryContainer>
        <TabContainer>
          <Tab
            $active={activeTab === "inProgress"}
            onClick={() => setActiveTab("inProgress")}
          >
            In Progress
            {inProgressCourses.length ? ` (${inProgressCourses.length})` : ""}
          </Tab>
          <Tab
            $active={activeTab === "completed"}
            onClick={() => setActiveTab("completed")}
          >
            Completed
            {completedCourses.length ? ` (${completedCourses.length})` : ""}
          </Tab>
        </TabContainer>
        {visibleCourses.length
          ? visibleCourses.map(renderCourseCard)
          : (
            <EmptyLibraryMessage>{emptyMessage}</EmptyLibraryMessage>
          )}
      </LibraryContainer>
      <CertificateShareSurfaces
        share={certificateShare}
        isDarkMode={isDarkMode}
      />
      <BottomNavBar></BottomNavBar>
    </div>
  );
}

export default Library;
